import type { ReactElement } from "react"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import ContentWithImageOrg from "@onestore/hel/dist/components/organisms/ContentWithImageOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CopyToClipboardButtonWithText from "@gatsby-plugin-generic-page/components/CopyToClipboardButtonWithText"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import Media from "@gatsby-plugin-generic-page/components/Media"
import Paragraph from "@gatsby-plugin-generic-page/components/Paragraph"
import TextWithPrice from "@gatsby-plugin-generic-page/components/TextWithPrice"
import ToggleWithLinkButtons from "@gatsby-plugin-generic-page/components/ToggleWithLinkButtons"
import type { ContentWithImageSectionFlatData } from "@gatsby-plugin-generic-page/fragments/contentWithImageSection"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

function ContentWithImageSection({
  header,
  toggleWithLinkButtons,
  paragraphs,
  responsiveImages,
  isReversed,
  hasImageStickyToBottom,
  isStickyToTop,
  textWithPrice,
  ctaElement,
  copyToClipboardButtonWithText,
  isReversedOnMobile,
  hasReversedButtonsOnMobile,
  sectionSettings,
}: ContentWithImageSectionFlatData): ReactElement<ContentWithImageSectionFlatData> {
  const headerMobileTopSpace =
    isReversedOnMobile && hasImageStickyToBottom ? 4 : 0

  const buttonsJSX = (
    <div>
      {textWithPrice ? (
        <PushOrg bottomSpace={3}>
          <UdsThemeProvider>
            <TextWithPrice {...textWithPrice} hasFlatRenderMode />
          </UdsThemeProvider>
        </PushOrg>
      ) : null}

      {ctaElement ? (
        <CtaElement ctaElement={ctaElement} align={header.align} />
      ) : null}

      {copyToClipboardButtonWithText ? (
        <CopyToClipboardButtonWithText {...copyToClipboardButtonWithText} />
      ) : null}
    </div>
  )

  const headerElement = (
    <div
      className={cn(
        "max-lg:w-full",
        hasReversedButtonsOnMobile ? "mb-4 lg:mb-5" : "mb-5",
        headerMobileTopSpace ? "mt-5 lg:mt-0" : undefined
      )}
    >
      <Header {...header} />
    </div>
  )

  const toggleElement = !isEmpty(toggleWithLinkButtons) ? (
    <div className="max-lg:w-full mb-5 lg:mb-6">
      <ToggleWithLinkButtons {...toggleWithLinkButtons} />
    </div>
  ) : null

  const contentElement = (
    <div
      className={cn(
        "max-lg:w-full",
        hasReversedButtonsOnMobile &&
          "max-lg:flex max-lg:flex-col-reverse max-lg:gap-4"
      )}
    >
      {!isEmpty(paragraphs) &&
        paragraphs.map((paragraph, index) => (
          <div
            className={hasReversedButtonsOnMobile ? "lg:mb-5" : "mb-5"}
            key={`paragraph-${paragraph.content.text}-${index}`}
          >
            <Paragraph {...paragraph} />
          </div>
        ))}

      {buttonsJSX}
    </div>
  )

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <ContentWithImageOrg
          hasImageStickyToBottom={hasImageStickyToBottom}
          isStickyToTop={isStickyToTop}
          isReversedOnLarge={isReversed}
          isReversedOnMobile={isReversedOnMobile}
          image={<Media media={responsiveImages} />}
          content={
            <ConditionalWrapper
              condition={!isEmpty(header.align)}
              wrapper={(children) => (
                <AlignContentOrg
                  horizontalAlign={{
                    large: header.align?.large.horizontalAlign || "default",
                    medium: header.align?.medium.horizontalAlign || "default",
                    small: header.align?.small.horizontalAlign || "default",
                  }}
                  horizontalAlignType="both"
                >
                  {children}
                </AlignContentOrg>
              )}
            >
              {toggleElement}

              {headerElement}

              {contentElement}
            </ConditionalWrapper>
          }
        />
      </BaseContainer>
    </BaseSection>
  )
}

export default ContentWithImageSection
